/**
 * Pre-paint theme bootstrap (TES-1578).
 *
 * Lives here rather than inline in `app/layout.tsx` so it can be unit-tested —
 * the two bugs it fixes are both invisible to a single post-load assertion.
 *
 * ## Bug 1 — the script was not actually pre-paint
 *
 * It previously shipped as `<Script strategy="beforeInteractive">`. In the App
 * Router that does NOT emit a parser-blocking inline script: Next pushes the
 * body into `self.__next_s` and its bootstrap chunk replays it after the async
 * chunks load. Measured on production, `.light` landed ~600ms after commit, so
 * every light-theme visitor got ~0.6s of the dark canvas first, on every page.
 * A plain inline `<script>` runs synchronously during head parsing instead —
 * the same reason the JSON-LD tags in `app/layout.tsx` are plain scripts
 * (TES-833). CSP already allows `script-src 'unsafe-inline'`.
 *
 * ## Bug 2 — React strips the class back off
 *
 * `<html>` is React-rendered, and its `className` prop carries only the font
 * variables. On `/sounds/collection/[slug]` React's hydration pass normalises
 * `<html>`'s attributes to exactly what the server rendered, ~1s after load,
 * which silently drops `.light` and flips a light-theme user to dark against
 * their saved preference. Verified with a CDP `attribute-modified` breakpoint:
 * the write comes from React's own work loop, not from any app code. A
 * `data-*` attribute does not survive either — React removes unknown
 * attributes on `<html>` in the same pass — so the flag cannot simply be moved
 * off `className`.
 *
 * The repair is a MutationObserver that re-asserts the preference. Its callback
 * is a microtask, so it runs at the end of the same task as React's commit,
 * before the browser paints that frame — the user sees no flash. This mirrors
 * the `strip-vercel-deployment-id` observer already in `app/layout.tsx`, which
 * exists for the same class of problem.
 *
 * PRODUCT.md: dark is the default, light is an equal-quality secondary theme —
 * so "just force dark" is not an acceptable resolution.
 */

export const THEME_STORAGE_KEY = "tls.theme";

/** Class applied to `<html>` for the light theme. Matches app/globals.css. */
export const THEME_LIGHT_CLASS = "light";

/**
 * Set on <html> for the single frame a theme flip takes. app/globals.css
 * zeroes every transition while it is present, so the repaint lands at once
 * instead of each surface animating its colour independently (which made the
 * rounded nav pill visibly morph in the top-left corner).
 */
export const THEME_SWITCHING_CLASS = "tls-theme-switching";

export type ThemeName = "dark" | "light";

/**
 * The stored preference, or null when absent/unreadable.
 *
 * Client components must resolve the active theme from this FIRST and only
 * fall back to the `<html>` class — the class is transiently absent between
 * React's hydration pass stripping it and the bootstrap observer putting it
 * back, and a mount effect can land in exactly that gap (TES-1578). Mirrors
 * `prefersLight()` in THEME_INIT_SCRIPT below; keep the two in step.
 */
export function readStoredTheme(): ThemeName | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // Private browsing may throw.
  }
  return null;
}

/** The active theme: stored preference first, then whatever is on `<html>`. */
export function resolveActiveTheme(): ThemeName {
  return (
    readStoredTheme() ??
    (document.documentElement.classList.contains(THEME_LIGHT_CLASS) ? "light" : "dark")
  );
}

export const THEME_INIT_SCRIPT = `
(function () {
  var root = document.documentElement;

  function prefersLight() {
    try {
      var stored = localStorage.getItem('${THEME_STORAGE_KEY}');
      if (stored === 'light') return true;
      if (stored === 'dark') return false;
    } catch (e) {}
    // Storage unavailable (private browsing) or no preference yet: trust
    // whatever is on the element so this never fights a live toggle, and so a
    // first-time visitor still gets the dark default.
    return root.classList.contains('${THEME_LIGHT_CLASS}');
  }

  var applying = false;
  var restores = 0;
  var observer = null;

  function apply() {
    if (applying) return;
    var light = prefersLight();
    if (light === root.classList.contains('${THEME_LIGHT_CLASS}')) return;
    // Cap restores so a translator that rewrites html.className in its own
    // observer cannot ping-pong with this one until Safari reloads the tab.
    // Hydration strips .light a handful of times (TES-1578); 16 is well above
    // that and well below an infinite loop.
    if (restores >= 16) {
      if (observer) observer.disconnect();
      return;
    }
    restores++;
    applying = true;
    try {
      if (light) {
        root.classList.add('${THEME_LIGHT_CLASS}');
      } else {
        root.classList.remove('${THEME_LIGHT_CLASS}');
      }
    } finally {
      applying = false;
    }
  }

  apply();

  try {
    observer = new MutationObserver(apply);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class']
    });
  } catch (e) {}
})();
`.trim();
